// Third party
import React, { Component, ReactElement } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps, Redirect, Route, Switch } from 'react-router-dom';
import qs from 'qs';
import debounce from 'lodash/debounce';
import { SortingRule } from 'react-table';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '../../../utils/3rd-party/heap';
import { formatMessage } from '../../../utils/i18n';
import notification from '../../../utils/notification';
import { canAccessTaxAnalysis, canAccessUploadCSV } from '@/utils/feature-access';
import { userHasPermissionEnabled, PERMISSIONS, userHasPermission } from '../../../utils/permissions';
import { showError } from '../../../utils/errors';
import { portfolioIsNew, PORTFOLIO_TYPES, getEmptyPortfolio, PortfolioEditorContext } from '../../../utils/portfolios';
import { HoldingAllocationMode } from '@/enums/Portfolio';
import { clonePortfolioAsProxy, isEnterpriseModel, loadPortfolioIfHoldingsAreMissing, loadPortfoliosIfHoldingsAreMissing } from '@/utils/portfolio/utils';
import { withLoaderAction } from '@/utils/promise';
import disclaimers from '../../../utils/disclaimers';
import { NAV_PATHS } from '../../../utils/constants';

// Components
import { Header } from '@/views/components/layout';
import { ErrorBoundary } from '../../components/errors';
import { EditPortfolioModal, DeletePortfoliosModal, EditBenchmarkModal } from '../../components/modals';
import MyModelsTab from './MyModelsTab';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';
import { Stack } from '@capintel/component-lib/stack';
import { SecondaryNavigation } from '../../components/navigation';
import AllBenchmarkPortfolios from './AllBenchmarkPortfolios';
import EnterpriseModelsTab from './EnterpriseModelsTab';

// Actions
import { ReduxState } from '@/redux/modules/types';
import {
  addPortfoliosToComparison,
  addBenchmarkPortfolioToComparisonById,
  addBenchmarkPortfoliosToNewComparison,
  ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON,
  ADD_PORTFOLIOS_TO_COMPARISON,
} from '@/redux/modules/comparison';
import { shouldClosePortfolioEditor } from '@/redux/modules/ai-statement-reader-multi-portfolio';
import {
  getModelsPortfolios,
  deletePortfolios,
  getPortfolioById,
  DELETE_PORTFOLIOS,
  clonePortfolio,
  clearNewlyCreatedModelIds,
  addNewlyCreatedModelId,
} from '@/redux/modules/portfolios';
import {
  savePortfolio,
  loadPortfolioForEdit,
  resetPortfolio,
  saveModelCopyToDraftPortfolio,
  setPortfolioEditorContext,
  setPortfolioEditorIsCreatingModel,
} from '@/redux/modules/edit-portfolio';
import { getMyBenchmarkPortfolios, deleteBenchmarkPortfolios, DELETE_BENCHMARK_PORTFOLIOS } from '@/redux/modules/comparison-benchmarks/benchmark-portfolios';
import { loadBenchmarkForEdit, togglePortfolioEditor } from '@/redux/modules/common/actionCreators';
import { saveBenchmark, loadNewBenchmark } from '@/redux/modules/edit-benchmark';
import { incrementLimit, consumeLimit } from '@/redux/modules/rate-limits';
import { selectIsLoadingEvery, selectIsLoadingParamsEvery, selectIsLoadingSome, startLoaderAction, stopLoaderAction } from '@/redux/modules/ui/loaders';
import { setEnterpriseDetails } from '@/redux/modules/enterprise';
import type { PortfoliosTable } from '../../components/tables';
import { logNewPortfolio } from '../../../services/usage-log';

// Types
import { RateLimitType } from '@/redux/modules/types/rate-limits';
import { CurrentContact } from '@/redux/modules/types/contacts';
import { modelType } from '@/types/data';
import Dictionary from '@/types/dictionary';
import Portfolio from '@/types/portfolio';
import PaginatedPortfolios from '@/types/paginated-portfolios';
import ComparisonItem from '@/types/comparison-item';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import User from '@/types/user';
import AppMode from '@/types/app-mode';
import PortfolioUploadModal from '@/views/components/modals/portfolio-upload/PortfolioUploadModal';
import useThemeVersion from '@/hooks/useThemeVersion';
import LegacyModels from './LegacyModels';
import PortfolioType from '@/types/portfolio-type';
import FormattedMessageV2, { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type DispatchProps = {
  getModelsPortfolios: (type?: modelType, query?: string, sorting?: SortingRule[], nextToken?: string) => Promise<Portfolio[] | PaginatedPortfolios>;
  deletePortfolios: (portfolios: Portfolio[], isModel: boolean) => Promise<{ portfolioIds: string[] }>;
  savePortfolio: (portfolio: Portfolio) => Promise<Portfolio>;
  loadPortfolioForEdit: (portfolio: Portfolio, comparisonItem?: ComparisonItem, isPinned?: boolean) => Promise<void>;
  resetPortfolio: () => void;
  addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
  getMyBenchmarkPortfolios: (query?: string, nextToken?: string, sorting?: SortingRule[]) => Promise<BenchmarkPortfolio[]>;
  addBenchmarkPortfoliosToNewComparison: (benchmarkPortfolios: BenchmarkPortfolio[]) => Promise<void>;
  deleteBenchmarkPortfolios: (benchmarkPortfolios: BenchmarkPortfolio[]) => Promise<{ benchmarkPortfolioIds: string[] }>;
  loadBenchmarkForEdit: (benchmarkPortfolio: BenchmarkPortfolio, item?: ComparisonItem) => void;
  saveBenchmark: (benchmarkPortfolio: BenchmarkPortfolio) => Promise<BenchmarkPortfolio>;
  addBenchmarkPortfolioToComparisonById: (id: string) => Promise<void>;
  loadNewBenchmark: () => void;
  incrementLimit: (limitType: RateLimitType) => void;
  consumeLimit: (limitType: RateLimitType) => void;
  saveModelCopyToDraftPortfolio: (portfolio: Portfolio) => Promise<Portfolio>;
  setEnterpriseDetails: (enterprise: EnterpriseDetails) => void;
  logNewPortfolio: () => Promise<void>;
  togglePortfolioEditor: (show: boolean) => void;
  getPortfolioById: (
    id: string,
    portfolioType?: string,
    contact?: string,
    enterprise?: any,
    errorWhiteList?: string[],
    source?: Portfolio,
  ) => Promise<Portfolio>;
  startLoaderAction: (name: string) => void;
  stopLoaderAction: (name: string) => void;
  setPortfolioEditorContext: (editorContext: PortfolioEditorContext) => void;
  setPortfolioEditorIsCreatingModel: (isCreatingModel?: boolean) => void;
  clonePortfolio: (portfolio: Portfolio) => void;
  clearNewlyCreatedModelIds: () => void;
  addNewlyCreatedModelId: (portfolioId: string) => void;
};

type StateProps = {
  editPortfolio: Portfolio;
  evaluatingPortfolioForComparison: boolean;
  portfolios: {
    myModels: Portfolio[];
    nextMyModelsToken?: string;
    enterpriseModels: Portfolio[];
    nextEnterpriseModelsToken?: string;
    newlyCreatedModelIds?: string[];
  };
  portfolio: Portfolio;
  enterprise: EnterpriseDetails;
  benchmarkPortfolios: {
    benchmarkPortfolios: BenchmarkPortfolio[];
    nextBenchmarkPortfoliosToken?: string;
  };
  currentContact?: CurrentContact;
  comparingBenchmarkPortfolioIds: string[];
  comparingModelIds: string[];
  isDeletingBenchmarkPortfolios: boolean;
  DeletingModelPortfolios: boolean;
  showEditPortfolioModal: boolean;
  shouldCloseEditor: boolean;
};

type Props = RouteComponentProps &
  StateProps &
  DispatchProps & {
    user: User;
  };

type State = {
  initialized: boolean;
  redirect?: string;
  selectedPortfolioForDelete?: Portfolio[];
  showPortfolioUploadModal: boolean;
  showDeletePortfolioModal: boolean;
  query: string;
  searching: boolean;
  selectedBenchmarkPortfolioForDelete?: BenchmarkPortfolio[];
  showDeleteBenchmarkPortfolioModal: boolean;
  showEditBenchmarkModal: boolean;
  pendingComparison: boolean;
  sorting: SortingRule[];
};

const MODELS_TABS = {
  MODELS: '/models',
  ENTERPRISE: '/models/enterprise',
  CUSTOM_BENCHMARKS: '/models/custom-benchmarks',
};

const Models = (props: Props): ReactElement => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <ModelsV2 {...props} />;
  }
  return <LegacyModels {...props} />;
};

class ModelsV2 extends Component<Props> {
  myModelsPortfoliosTable = React.createRef<PortfoliosTable<Portfolio>>();

  myBenchmarkPortfoliosTable = React.createRef<PortfoliosTable<BenchmarkPortfolio>>();

  readonly state: State = {
    query: '',
    searching: false,
    initialized: false,
    showPortfolioUploadModal: false,
    showEditBenchmarkModal: false,
    showDeletePortfolioModal: false,
    showDeleteBenchmarkPortfolioModal: false,
    pendingComparison: false,
    sorting: [],
  };

  searchMyModelsPortfolios = debounce((query: string) => {
    const { user } = this.props;
    const { sorting } = this.state;
    if (!userHasPermission(user, PERMISSIONS.USER_MODELS)) {
      return null;
    }

    this.setState({ query, searching: true });
    this.props.getModelsPortfolios(modelType.User, query, sorting).finally(() => {
      this.setState({ searching: false });
    });
  }, 250);

  searchEnterprisePortfolios = debounce((query: string) => {
    const { enterprise, user } = this.props;
    const { sorting } = this.state;
    if (!enterprise || !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST)) {
      return null;
    }
    this.setState({ query, searching: true });
    this.props.getModelsPortfolios(modelType.Enterprise, query, sorting).finally(() => {
      this.setState({ searching: false });
    });
  }, 250);

  searchAllBenchmarkPortfolios = debounce((query: string) => {
    const { sorting } = this.state;
    this.setState({ query, searching: true });

    this.props.getMyBenchmarkPortfolios(query, undefined, sorting).finally(() => {
      this.setState({ searching: false });
    });
  }, 250);

  componentDidMount() {
    const { user } = this.props;
    const promises: Promise<unknown>[] = [];

    if (user && userHasPermission(user, PERMISSIONS.USER_MODELS)) {
      promises.push(this.props.getModelsPortfolios(modelType.User));
    }

    if (user && user.enterprise) {
      this.props.setEnterpriseDetails(user.enterprise);
      if (userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST)) {
        promises.push(this.props.getModelsPortfolios(modelType.Enterprise));
      }
    }
    // Only wait for the model and enterprise portfolios.
    // Everything else is not needed for initial load.
    Promise.all(promises).finally(() => {
      this.setState({ initialized: true });
    });

    if (!!user && userHasPermission(user, PERMISSIONS.BENCHMARKS)) {
      this.props.getMyBenchmarkPortfolios();
    }

    const query = qs.parse(this.props.location.search, { ignoreQueryPrefix: true });
    if (query.newPortfolio) {
      this.props.history.push(this.props.location.pathname);
      this.props.togglePortfolioEditor(true);
    }

    if (query.newBenchmarkPortfolio) {
      this.props.history.push(this.props.location.pathname);
      this.setState({ showEditBenchmarkModal: true });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { location, user } = this.props;
    const { location: prevLocation } = prevProps;

    if (location.pathname !== prevLocation.pathname) {
      this.setState({ query: null, initialized: false });
      let promise: Promise<unknown>;

      switch (this.getSelectedTabValue()) {
        case MODELS_TABS.MODELS: {
          if (userHasPermission(user, PERMISSIONS.USER_MODELS)) {
            promise = this.props.getModelsPortfolios(modelType.User);
          } else {
            promise = Promise.resolve();
          }
          break;
        }
        case MODELS_TABS.ENTERPRISE: {
          if (userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST)) {
            promise = this.props.getModelsPortfolios(modelType.Enterprise);
          } else {
            promise = Promise.resolve();
          }
          break;
        }
        case MODELS_TABS.CUSTOM_BENCHMARKS: {
          promise = this.props.getMyBenchmarkPortfolios();
          break;
        }
        default: {
          promise = Promise.resolve();
        }
      }

      promise.finally(() => {
        this.setState({ initialized: true });
      });
    }
  }

  componentWillUnmount() {
    const { clearNewlyCreatedModelIds } = this.props;
    clearNewlyCreatedModelIds();
  }

  getTabOptions = () => {
    const { user } = this.props;

    const items = [
      {
        value: MODELS_TABS.MODELS,
        label: formatMessage('ModelsV2.Nav.Models'),
        exact: true,
        hidden: !user || !userHasPermission(user, PERMISSIONS.USER_MODELS),
      },
      {
        value: MODELS_TABS.ENTERPRISE,
        label: formatMessage('ModelsV2.Nav.Enterprise'),
        exact: true,
        hidden: !user || !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST),
      },
      {
        value: MODELS_TABS.CUSTOM_BENCHMARKS,
        label: formatMessage('ModelsV2.Nav.BenchmarkPortfolios'),
        hidden: !user || !userHasPermission(user, PERMISSIONS.BENCHMARKS),
      },
    ];

    return items.filter(i => !i.hidden);
  };

  onSortingChange = (sorting: SortingRule[]) => {
    const { user } = this.props;

    this.setState({ sorting, initialized: false });
    let promise: Promise<unknown>;
    switch (this.getSelectedTabValue()) {
      case MODELS_TABS.MODELS:
        if (userHasPermission(user, PERMISSIONS.USER_MODELS)) {
          promise = this.props.getModelsPortfolios(modelType.User, this.state.query, sorting);
        } else {
          promise = Promise.resolve();
        }
        break;
      case MODELS_TABS.ENTERPRISE:
        if (userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST)) {
          promise = this.props.getModelsPortfolios(modelType.Enterprise, this.state.query, sorting);
        } else {
          promise = Promise.resolve();
        }
        break;
      case MODELS_TABS.CUSTOM_BENCHMARKS:
        promise = this.props.getMyBenchmarkPortfolios(this.state.query, undefined, sorting);
        break;
      default:
        promise = Promise.resolve();
        break;
    }

    promise.finally(() => {
      this.setState({ initialized: true });
    });
  };

  getSelectedTabValue = () => {
    const tab = this.getTabOptions().find(item => item.value === this.props.location.pathname);
    return tab ? tab.value : null;
  };

  onCreatePortfolioClick = () => {
    const {
      clearNewlyCreatedModelIds,
      resetPortfolio,
      setPortfolioEditorContext,
      user,
      setPortfolioEditorIsCreatingModel,
      loadPortfolioForEdit,
      logNewPortfolio,
    } = this.props;

    clearNewlyCreatedModelIds();
    resetPortfolio();
    setPortfolioEditorContext(PortfolioEditorContext.PORTFOLIO);

    if (canAccessTaxAnalysis(user)) {
      setPortfolioEditorIsCreatingModel(true);
    }

    loadPortfolioForEdit(getEmptyPortfolio(PORTFOLIO_TYPES.TEMPLATE));
    logNewPortfolio();
  };

  onCreateBenchmarkClick = () => {
    this.props.loadNewBenchmark();
    this.openEditBenchmarkModal();
  };

  setPendingComparison = (pendingComparison?: boolean) => {
    this.setState({ pendingComparison: pendingComparison !== undefined ? pendingComparison : false });
  };

  closeEditPortfolioModal = () => {
    this.props.resetPortfolio();

    if (canAccessTaxAnalysis(this.props.user)) {
      this.props.setPortfolioEditorIsCreatingModel(false);
    }

    this.props.togglePortfolioEditor(false);
  };

  openEditBenchmarkModal = () => {
    this.setState({ showEditBenchmarkModal: true });
  };

  closeEditBenchmarkModal = () => {
    this.setState({ showEditBenchmarkModal: false });
  };

  savePortfolio = (portfolio: Portfolio): Promise<Portfolio> => {
    // Save portfolio as model
    const newPortfolio = {
      ...portfolio,
      mine: true,
      contact: undefined,
    };

    const { savePortfolio: savePortfolioAction, shouldCloseEditor, consumeLimit, addNewlyCreatedModelId, getModelsPortfolios } = this.props;
    const { pendingComparison, query, sorting } = this.state;

    return savePortfolioAction(newPortfolio).then(savedPortfolio => {
      consumeLimit(RateLimitType.MODELS);

      // Track newly created model for highlighting
      if (portfolioIsNew(portfolio) && savedPortfolio._id) {
        addNewlyCreatedModelId(savedPortfolio._id);
      }

      if (shouldCloseEditor) {
        this.closeEditPortfolioModal();
      }

      this.closeEditPortfolioModal();
      if (pendingComparison) {
        this.onComparePortfolio(newPortfolio);
      } else {
        this.setState({ initialized: false });
        getModelsPortfolios(modelType.User, query, sorting).finally(() => {
          this.setState({ initialized: true });
        });
      }
      return savedPortfolio;
    });
  };

  saveBenchmark = (benchmarkPortfolio: BenchmarkPortfolio) =>
    this.props.saveBenchmark(benchmarkPortfolio).then(() => {
      this.closeEditBenchmarkModal();
      this.props.getMyBenchmarkPortfolios();
    });

  onEditPortfolio = (inPortfolio: Portfolio, pendingComparison?: boolean) => {
    const { user, getPortfolioById } = this.props;

    this.props.setPortfolioEditorContext(PortfolioEditorContext.PORTFOLIO);

    loadPortfolioIfHoldingsAreMissing(inPortfolio, getPortfolioById)
      .then(portfolio => {
        const toLoad = disclaimers.portfolioHasMissingOrInvalidHoldings(portfolio) ? clonePortfolioAsProxy(portfolio) : portfolio;

        if (user && userHasPermissionEnabled(user, PERMISSIONS.PORTFOLIO_UNITS_MODE)) {
          toLoad.allocationMode = HoldingAllocationMode.Units;
        }

        if (canAccessTaxAnalysis(user)) {
          this.props.setPortfolioEditorIsCreatingModel(true);
        }

        this.props
          .loadPortfolioForEdit(toLoad)
          .then(() => {
            this.setPendingComparison(pendingComparison);
          })
          .catch(showError);
      })
      .catch(showError);
  };

  onClonePortfolio = (portfolio: Portfolio, type: PortfolioType) => {
    if (canAccessTaxAnalysis(this.props.user)) {
      this.props.setPortfolioEditorIsCreatingModel(true);
    }

    this.props.getPortfolioById(portfolio._id!, type).then((p: Portfolio) => {
      this.props.clonePortfolio(p);
    });
  };

  onEditBenchmarkPortfolio = (benchmarkPortfolio: BenchmarkPortfolio) => {
    this.props.loadBenchmarkForEdit(benchmarkPortfolio);
    this.openEditBenchmarkModal();
  };

  onComparePortfolio = async (inPortfolio: Portfolio) => {
    heapUtils.track(HEAP_CUSTOM_EVENTS.LOAD_MODEL, { isEnterpriseModel: isEnterpriseModel() });
    const { user, currentContact, getPortfolioById } = this.props;

    const portfolio = await loadPortfolioIfHoldingsAreMissing(inPortfolio, getPortfolioById);
    if (disclaimers.portfolioHasMissingOrInvalidHoldings(portfolio)) {
      this.onEditPortfolio(portfolio, true);
    } else {
      let localPortfolio = portfolio;
      if (user != null && user.appConfig.mode === AppMode.CONTACT && currentContact != null) {
        localPortfolio = await this.props.saveModelCopyToDraftPortfolio(portfolio);
      }

      await this.props.addPortfoliosToComparison([localPortfolio]).catch(err => {
        notification.errorFromKey(err.errorKey);
      });

      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON, { isEnterpriseModel: isEnterpriseModel() });
      this.props.togglePortfolioEditor(false);
      this.setState({ redirect: NAV_PATHS.COMPARISON, pendingComparison: false });
    }
  };

  onComparePortfolios = async (portfolioList: Portfolio[]) => {
    const { getPortfolioById, startLoaderAction, stopLoaderAction } = this.props;

    await withLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type, startLoaderAction, stopLoaderAction, async () => {
      const portfolios = await loadPortfoliosIfHoldingsAreMissing(portfolioList, getPortfolioById);

      const portfolioWithMissingHolding = portfolios.find(portfolio => disclaimers.portfolioHasMissingOrInvalidHoldings(portfolio));
      if (portfolioWithMissingHolding) {
        this.onEditPortfolio(portfolioWithMissingHolding, true);
        return;
      }

      await this.props.addPortfoliosToComparison(portfolios).catch(err => {
        notification.errorFromKey(err.errorKey);
      });

      this.props.togglePortfolioEditor(false);
      this.setState({ redirect: NAV_PATHS.COMPARISON, pendingComparison: false });
    });
  };

  onCompareBenchmarkPortfolio = (benchmarkPortfolio: BenchmarkPortfolio) => {
    this.props
      .addBenchmarkPortfolioToComparisonById(benchmarkPortfolio._id)
      .then(() => {
        this.setState({ redirect: NAV_PATHS.COMPARISON });
      })
      .catch(showError);
  };

  onCompareBenchmarkPortfolios = (benchmarkPortfolios: BenchmarkPortfolio[]) => {
    this.props
      .addBenchmarkPortfoliosToNewComparison(benchmarkPortfolios)
      .then(() => {
        this.setState({ redirect: NAV_PATHS.COMPARISON });
      })
      .catch(showError);
  };

  onDeletePortfolio = (portfolios: Portfolio[]) => {
    this.setState({ selectedPortfolioForDelete: portfolios });
    this.showDeletePortfolioModal();
  };

  onDeleteBenchmarkPortfolio = (benchmarkPortfolios: BenchmarkPortfolio[]) => {
    this.setState({ selectedBenchmarkPortfolioForDelete: benchmarkPortfolios });
    this.showDeleteBenchmarkPortfolioModal();
  };

  showDeletePortfolioModal = () => {
    this.setState({ showDeletePortfolioModal: true });
  };

  hideDeletePortfolioModal = () => {
    this.setState({ showDeletePortfolioModal: false });
  };

  showDeleteBenchmarkPortfolioModal = () => {
    this.setState({ showDeleteBenchmarkPortfolioModal: true });
  };

  hideDeleteBenchmarkPortfolioModal = () => {
    this.setState({ showDeleteBenchmarkPortfolioModal: false });
  };

  closePortfolioUploadModal = () => {
    this.setState({ showPortfolioUploadModal: false });
  };

  onCancelDeletePortfolio = () => {
    this.setState({ selectedPortfolioForDelete: undefined });
    this.hideDeletePortfolioModal();
  };

  onConfirmDeletePortfolio = () => {
    const portfolios = this.state.selectedPortfolioForDelete;

    if (!portfolios) {
      return;
    }
    this.props.deletePortfolios(portfolios, true).then(res => {
      if (portfolios.length !== res.portfolioIds.length) {
        notification.error(formatMessage('Portfolios.DeleteFailure'));
      } else {
        notification.success(formatMessage('Portfolios.DeleteSuccess'));
      }
      this.myModelsPortfoliosTable.current?.unselectAll();
      this.hideDeletePortfolioModal();
    });
  };

  onCancelDeleteBenchmarkPortfolio = () => {
    this.setState({ selectedBenchmarkPortfolioForDelete: undefined });
    this.hideDeleteBenchmarkPortfolioModal();
  };

  onConfirmDeleteBenchmarkPortfolio = () => {
    const benchmarks = this.state.selectedBenchmarkPortfolioForDelete;

    if (!benchmarks) {
      return;
    }

    this.props.deleteBenchmarkPortfolios(benchmarks).then(res => {
      if (benchmarks.length !== res.benchmarkPortfolioIds.length) {
        notification.error(formatMessage('BenchmarkPortfolios.DeleteFailure'));
      } else {
        notification.success(formatMessage('BenchmarkPortfolios.DeleteSuccess'));
      }
      this.myBenchmarkPortfoliosTable.current?.unselectAll();
      this.hideDeleteBenchmarkPortfolioModal();
    });
  };

  onLoadMore = () => {
    const { query, sorting } = this.state;
    const {
      user,
      enterprise,
      portfolios: { nextMyModelsToken, nextEnterpriseModelsToken },
      benchmarkPortfolios: { nextBenchmarkPortfoliosToken },
    } = this.props;

    switch (this.getSelectedTabValue()) {
      case MODELS_TABS.MODELS: {
        if (user && userHasPermission(user, PERMISSIONS.USER_MODELS)) {
          this.props.getModelsPortfolios(modelType.User, query, sorting, nextMyModelsToken);
          break;
        }
        return null;
      }
      case MODELS_TABS.ENTERPRISE: {
        if (user && user.enterprise && enterprise && userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST)) {
          this.props.getModelsPortfolios(modelType.Enterprise, query, sorting, nextEnterpriseModelsToken);
          break;
        }
        return null;
      }
      case MODELS_TABS.CUSTOM_BENCHMARKS: {
        this.props.getMyBenchmarkPortfolios(query, nextBenchmarkPortfoliosToken);
        break;
      }
      default:
        return null;
    }
  };

  createDropdownItems = () => [
    {
      className: 'large-icon',
      node: formatMessage('Comparison.NewModel'),
      icon: 'create-portfolio1',
      onClick: this.onCreatePortfolioClick,
    },
    {
      className: 'large-icon',
      node: formatMessage('Comparison.UploadCSVFile'),
      icon: 'upload-file',
      onClick: () => this.setState({ showPortfolioUploadModal: true }),
      hidden: !canAccessUploadCSV(this.props.user),
    },
  ];

  showPortfolioUploadModal = () => {
    this.setState({ showPortfolioUploadModal: true });
  };

  getTableComponents = () => {
    const {
      portfolios: { myModels, nextMyModelsToken, enterpriseModels, nextEnterpriseModelsToken, newlyCreatedModelIds },
      benchmarkPortfolios: { benchmarkPortfolios, nextBenchmarkPortfoliosToken },
      evaluatingPortfolioForComparison,
      enterprise,
      user,
      comparingBenchmarkPortfolioIds,
      comparingModelIds,
    } = this.props;
    const { initialized, searching, query } = this.state;

    const hasBenchmarkPermission = !!user && userHasPermission(user, PERMISSIONS.BENCHMARKS);
    const hasMyModelListPermission = !!user && userHasPermission(user, PERMISSIONS.USER_MODELS);
    const hasEnterpriseModelsListPermission = !!user && userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST);

    return (
      <ErrorBoundary>
        <>
          <Switch>
            {hasMyModelListPermission && (
              <Route
                exact
                path={`${this.props.match.path}/`}
                render={() => (
                  <MyModelsTab
                    user={user}
                    initialized={initialized}
                    portfoliosTableRef={this.myModelsPortfoliosTable}
                    comparingModelIds={comparingModelIds}
                    query={query}
                    searching={searching}
                    models={myModels}
                    hasMore={!!nextMyModelsToken}
                    sortedColumns={this.state.sorting}
                    onSort={this.onSortingChange}
                    onSearch={this.searchMyModelsPortfolios}
                    onLoadMore={this.onLoadMore}
                    onEditPortfolio={this.onEditPortfolio}
                    onDeletePortfolio={this.onDeletePortfolio}
                    onClonePortfolio={portfolio => this.onClonePortfolio(portfolio, PORTFOLIO_TYPES.TEMPLATE)}
                    createDropdownItems={this.createDropdownItems}
                    onCompareSelected={this.onComparePortfolios}
                    onCreatePortfolio={this.onCreatePortfolioClick}
                    onUploadCSV={this.handleUploadCSV}
                    evaluatingPortfolioForComparison={evaluatingPortfolioForComparison}
                    highlightedIds={newlyCreatedModelIds}
                  />
                )}
              />
            )}

            {hasEnterpriseModelsListPermission && (
              <Route
                exact
                path={`${this.props.match.path}/enterprise`}
                render={() => (
                  <EnterpriseModelsTab
                    user={user}
                    initialized={initialized}
                    comparingModelIds={comparingModelIds}
                    query={query}
                    searching={searching}
                    models={enterpriseModels}
                    enterprise={enterprise}
                    onSearch={this.searchEnterprisePortfolios}
                    hasMore={!!nextEnterpriseModelsToken}
                    onSort={this.onSortingChange}
                    onLoadMore={this.onLoadMore}
                    onCompareSelected={this.onComparePortfolios}
                    onEditPortfolio={this.onEditPortfolio}
                    onDeletePortfolio={this.onDeletePortfolio}
                    onClonePortfolio={portfolio => this.onClonePortfolio(portfolio, PORTFOLIO_TYPES.FIRM)}
                    // TODO @deprecate Legacy Props
                    onCompare={this.onComparePortfolio}
                  />
                )}
              />
            )}

            {hasBenchmarkPermission && (
              <Route
                exact
                path={`${this.props.match.path}/custom-benchmarks`}
                render={() => (
                  <AllBenchmarkPortfolios
                    user={user}
                    initialized={initialized}
                    portfoliosTableRef={this.myBenchmarkPortfoliosTable}
                    onSearch={this.searchAllBenchmarkPortfolios}
                    comparingBenchmarkPortfolioIds={comparingBenchmarkPortfolioIds}
                    query={query}
                    searching={searching}
                    benchmarkPortfolios={benchmarkPortfolios}
                    hasMore={!!nextBenchmarkPortfoliosToken}
                    onSort={this.onSortingChange}
                    onLoadMore={this.onLoadMore}
                    onCompare={this.onCompareBenchmarkPortfolio}
                    onCompareSelected={this.onCompareBenchmarkPortfolios}
                    onCreatePortfolio={this.onCreateBenchmarkClick}
                    onEditPortfolio={this.onEditBenchmarkPortfolio}
                    onDeletePortfolio={this.onDeleteBenchmarkPortfolio}
                    evaluatingPortfolioForComparison={evaluatingPortfolioForComparison}
                  />
                )}
              />
            )}

            <Redirect to={this.props.match.path} />
          </Switch>
        </>
      </ErrorBoundary>
    );
  };

  handleUploadCSV = () => {
    this.props.setPortfolioEditorIsCreatingModel(true);
    this.showPortfolioUploadModal();
  };

  render() {
    const { user, portfolio, isDeletingBenchmarkPortfolios, isDeletingModelPortfolios, location } = this.props;

    const {
      showPortfolioUploadModal,
      redirect,
      showDeletePortfolioModal,
      selectedPortfolioForDelete,
      showEditBenchmarkModal,
      showDeleteBenchmarkPortfolioModal,
      selectedBenchmarkPortfolioForDelete,
    } = this.state;

    if (redirect) {
      return (
        <Redirect
          to={{
            pathname: redirect,
            state: { from: location },
          }}
        />
      );
    }

    const showCurrencies = !!user && userHasPermissionEnabled(user, PERMISSIONS.CURRENCY_CONVERSIONS);

    return (
      <>
        <Header />
        <div className="models-v2">
          <main id="content" className="models-container">
            <div className="models-body">
              <div className="models-header">
                <FormattedMessageV2 className="page-title" tagName={ALLOWED_HTML_TAGS.H1} toLocalize="ModelsV2.PageTitle" />
                <div className="page-pills">
                  <SecondaryNavigation className="models-header-navigation" items={this.getTabOptions()} />
                </div>
              </div>

              {this.getTableComponents()}
            </div>
          </main>
        </div>

        <PortfolioUploadModal show={showPortfolioUploadModal} onCancel={this.closePortfolioUploadModal} titleId="Modal.PortfolioUpload.UploadModelPortfolio" />

        <EditPortfolioModal
          show={this.props.showEditPortfolioModal}
          onSave={this.savePortfolio}
          onCancel={this.closeEditPortfolioModal}
          saveLabel={portfolioIsNew(portfolio) ? formatMessage('Portfolios.Edit.SaveModel') : undefined}
        />

        <EditBenchmarkModal show={showEditBenchmarkModal} onSave={this.saveBenchmark} onCancel={this.closeEditBenchmarkModal} showCurrencies={showCurrencies} />

        <DeletePortfoliosModal
          isDeleting={isDeletingModelPortfolios}
          portfolios={selectedPortfolioForDelete || []}
          show={showDeletePortfolioModal}
          onCancel={this.onCancelDeletePortfolio}
          onConfirm={this.onConfirmDeletePortfolio}
        />

        <DestructiveModal
          open={showDeleteBenchmarkPortfolioModal}
          onOpenChange={open => !open && this.onCancelDeleteBenchmarkPortfolio()}
          title={formatMessage('Benchmarks.DeleteModal.Title')}
          content={
            <Stack gap="150">
              <FormattedMessageV2 toLocalize="Benchmarks.DeleteModal.Body" />
              <ul>
                {(selectedBenchmarkPortfolioForDelete || []).map(p => (
                  <li key={p._id} data-private data-dd-privacy="mask">
                    {p.name}
                  </li>
                ))}
              </ul>
              <FormattedMessageV2 toLocalize="Benchmarks.DeleteModal.Confirmation" />
            </Stack>
          }
          confirmButtonText={formatMessage(`Benchmarks.DeleteModal.${isDeletingBenchmarkPortfolios ? 'ConfirmingButton' : 'ConfirmButton'}`)}
          cancelButtonText={formatMessage('Common.Cancel')}
          onConfirm={this.onConfirmDeleteBenchmarkPortfolio}
          onCancel={this.onCancelDeleteBenchmarkPortfolio}
          loading={isDeletingBenchmarkPortfolios}
        />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState) => {
  const benchmarkLoadingParams = selectIsLoadingParamsEvery(state, [ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON.type]);
  const comparingBenchmarkPortfolioIds = benchmarkLoadingParams.reduce((acc, param: Dictionary<unknown>) => {
    if (param.portfolioId) {
      return [...acc, param.portfolioId as string];
    }

    return acc;
  }, [] as string[]);
  const modelLoadingParams = selectIsLoadingParamsEvery(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]);
  const comparingModelIds = modelLoadingParams.reduce((acc, param: Dictionary<unknown>) => {
    if (param.portfolios) {
      return [...acc, ...(param.portfolios as Portfolio[]).map(portfolio => portfolio._id!)];
    }

    return acc;
  }, [] as string[]);

  return {
    editPortfolio: state.editPortfolio.portfolio,
    portfolios: state.portfolios,
    enterprise: state.enterprise.details,
    benchmarkPortfolios: state.comparisonBenchmarks.benchmarkPortfolios,
    portfolio: state.editPortfolio.portfolio,
    currentContact: state.contacts.current,
    comparingBenchmarkPortfolioIds,
    comparingModelIds,
    isDeletingBenchmarkPortfolios: selectIsLoadingEvery(state, [DELETE_BENCHMARK_PORTFOLIOS.type]),
    isDeletingModelPortfolios: selectIsLoadingEvery(state, [DELETE_PORTFOLIOS.type]),
    showEditPortfolioModal: state.comparison.portfolioEditor.isVisible,
    evaluatingPortfolioForComparison: selectIsLoadingSome(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]),
    shouldCloseEditor: shouldClosePortfolioEditor(state.aiStatementReaderMultiPortfolio),
  };
};

export default withRouter(
  connect(mapStateToProps, {
    getModelsPortfolios,
    getMyBenchmarkPortfolios,
    deletePortfolios,
    savePortfolio,
    loadPortfolioForEdit,
    resetPortfolio,
    addPortfoliosToComparison,
    addBenchmarkPortfoliosToNewComparison,
    deleteBenchmarkPortfolios,
    loadBenchmarkForEdit,
    saveBenchmark,
    addBenchmarkPortfolioToComparisonById,
    loadNewBenchmark,
    incrementLimit,
    consumeLimit,
    saveModelCopyToDraftPortfolio,
    setEnterpriseDetails,
    logNewPortfolio,
    togglePortfolioEditor,
    getPortfolioById,
    startLoaderAction,
    stopLoaderAction,
    setPortfolioEditorContext,
    setPortfolioEditorIsCreatingModel,
    clonePortfolio,
    clearNewlyCreatedModelIds,
    addNewlyCreatedModelId,
  })(Models),
);
